import { useState } from 'react';
import { API_BASE_URL } from '../../../../../config/api';

/**
 * Custom hook to manage channel settings state and save operations.
 * It no longer fetches data but works with the assistant object provided.
 * @param {Object} assistant - The assistant object
 * @param {Function} onUpdate - Callback to trigger a full assistant refresh
 * @returns {Object} Channel settings state and handlers
 */
const useChannelSettings = (assistant, onUpdate) => {
  // saving, error, and success are now objects keyed by channel type (e.g., 'WEB', 'VAPI')
  const [saving, setSaving] = useState({});
  const [error, setError] = useState({});
  const [success, setSuccess] = useState({});

  // The primary save handler for any channel configuration
  const handleSave = async (channelType, updatedConfig) => {
    if (!assistant?.id || !channelType) return;

    setSaving(prev => ({ ...prev, [channelType]: true }));
    setError(prev => ({ ...prev, [channelType]: '' }));
    setSuccess(prev => ({ ...prev, [channelType]: '' }));

    try {
      // Construct the payload according to UpdateAssistantDTO
      const payload = {
        configs: {
          [channelType]: updatedConfig,
        },
      };

      const response = await fetch(`${API_BASE_URL}/api/agents/${assistant.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to save settings');
      }

      setSuccess(prev => ({ ...prev, [channelType]: 'Settings saved successfully!' }));

      // After a delay, clear the success message and trigger the parent update.
      // This allows the user to see the feedback before the component re-renders.
      setTimeout(() => {
        setSuccess(prev => ({ ...prev, [channelType]: '' }));
        // Trigger a refresh of the entire assistant object in the parent component
        if (onUpdate) onUpdate();
      }, 3000);

    } catch (err) {
      console.error(`Error saving ${channelType} settings:`, err);
      setError(prev => ({ ...prev, [channelType]: err.message }));
    } finally {
      setSaving(prev => ({ ...prev, [channelType]: false }));
    }
  };

  return {
    saving,
    error,
    success,
    handleSave,
  };
};

export default useChannelSettings;